import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Calendar,
  RefreshCw,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Globe,
  MapPin,
  Home,
  TrendingUp,
  Activity,
  X,
  Eye
} from 'lucide-react';
import AdminAvailabilitySyncDetails from './AdminAvailabilitySyncDetails';

const AdminAvailability = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [countries, setCountries] = useState([]);
  const [zones, setZones] = useState([]);
  const [villas, setVillas] = useState([]);
  const [selectedSyncId, setSelectedSyncId] = useState(null);
  
  // Configuration de synchronisation
  const [syncConfig, setSyncConfig] = useState({
    syncType: 'full', // full, country, zone, manual
    selectedCountry: '',
    selectedZones: [], // Multiple zones
    selectedVillas: []  // Multiple villas
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les statistiques
      const statsResponse = await fetch('/api/availability/stats');
      const statsData = await statsResponse.json();
      if (statsData.success) {
        setStats(statsData.data);
      }

      // Charger l'historique
      const historyResponse = await fetch('/api/availability/sync-history?limit=20');
      const historyData = await historyResponse.json();
      if (historyData.success) {
        setSyncHistory(historyData.data);
      }

      // Charger les pays et zones disponibles
      const destinationsResponse = await fetch('/api/destinations?apply_exclusions=false');
      const destinationsData = await destinationsResponse.json();
      if (destinationsData.success) {
        setCountries(destinationsData.data.countries || []);
        setZones(destinationsData.data.zones || []);
      }

      // Charger la liste des villas pour la sélection manuelle
      const villasResponse = await fetch('/api/villas?limit=5000&apply_rules=false');
      const villasData = await villasResponse.json();
      if (villasData.success) {
        setVillas(villasData.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les zones par pays sélectionné
  const getZonesForCountry = (country) => {
    if (!country) return [];
    return zones.filter(z => z.country === country);
  };

  // Compter le nombre de villas par zone
  const getVillaCountByZone = (zone) => {
    return villas.filter(v => v.zone === zone && v.state === 1).length;
  };

  // Toggle zone dans la sélection multiple
  const toggleZone = (zone) => {
    setSyncConfig(prev => {
      const newZones = prev.selectedZones.includes(zone)
        ? prev.selectedZones.filter(z => z !== zone)
        : [...prev.selectedZones, zone];
      return { ...prev, selectedZones: newZones };
    });
  };

  // Toggle villa dans la sélection manuelle
  const toggleVilla = (villaId) => {
    setSyncConfig(prev => {
      const newVillas = prev.selectedVillas.includes(villaId)
        ? prev.selectedVillas.filter(id => id !== villaId)
        : [...prev.selectedVillas, villaId];
      return { ...prev, selectedVillas: newVillas };
    });
  };

  const handleSync = async () => {
    // Protection contre les doubles clics
    if (syncing) {
      console.warn('Synchronisation déjà en cours, ignorant...');
      return;
    }

    let confirmMessage = '';
    let payload = { syncType: syncConfig.syncType };

    switch (syncConfig.syncType) {
      case 'full':
        confirmMessage = `Lancer une synchronisation COMPLÈTE de toutes les villas actives ?\n\nCela peut prendre plusieurs minutes.`;
        break;
      
      case 'country':
        confirmMessage = `Synchroniser toutes les villas de ${syncConfig.selectedCountry} ?\n\nCela peut prendre quelques minutes.`;
        payload.country = syncConfig.selectedCountry;
        break;
      
      case 'zone':
        if (syncConfig.selectedZones.length === 0) {
          alert('⚠️ Veuillez sélectionner au moins une zone');
          return;
        }
        confirmMessage = `Synchroniser ${syncConfig.selectedZones.length} zone(s) sélectionnée(s) ?\n\nZones: ${syncConfig.selectedZones.join(', ')}`;
        payload.zones = syncConfig.selectedZones;
        break;
      
      case 'manual':
        if (syncConfig.selectedVillas.length === 0) {
          alert('⚠️ Veuillez sélectionner au moins une villa');
          return;
        }
        confirmMessage = `Synchroniser ${syncConfig.selectedVillas.length} villa(s) sélectionnée(s) ?`;
        payload.villaIds = syncConfig.selectedVillas;
        break;
      
      default:
        return;
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setSyncing(true);
    try {
      const response = await fetch('/api/availability/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ Synchronisation terminée avec succès !\n\n` +
          `📊 Villas traitées: ${data.data.villasProcessed}/${data.data.villasTotal}\n` +
          `📅 Dates mises à jour: ${data.data.datesUpdated}\n` +
          `🆕 Nouvelles réservations: ${data.data.datesAdded}\n` +
          `⏱️ Durée: ${data.data.duration}\n` +
          (data.data.errors > 0 ? `⚠️ Erreurs: ${data.data.errors}` : '')
        );
        loadData();
      } else {
        alert(`❌ Erreur: ${data.error || data.message}`);
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert('❌ Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getSyncStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSyncStatusBadge = (status) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      running: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };

    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status === 'completed' && '✅'}
        {status === 'running' && '⏳'}
        {status === 'failed' && '❌'}
        {status === 'cancelled' && '🚫'}
        {' '}{status.toUpperCase()}
      </Badge>
    );
  };

  const handleCancelSync = async (syncId) => {
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir annuler cette synchronisation ?\n\nID: ${syncId}`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`/api/availability/sync/${syncId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Synchronisation annulée avec succès');
        loadData();
      } else {
        alert(`❌ Erreur: ${data.error || data.message}`);
      }
    } catch (error) {
      console.error('Cancel sync error:', error);
      alert('❌ Erreur lors de l\'annulation');
    }
  };

  const getSyncTypeLabel = (syncType, country, zone, villaId) => {
    if (syncType === 'single_villa') return `Villa #${villaId}`;
    if (syncType === 'country') return `Pays: ${country}`;
    if (syncType === 'zone') return `Zone: ${zone}`;
    return 'Synchronisation Complète';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-blue-600" />
            Gestion des Disponibilités
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Synchronisation des calendriers de réservation depuis l'API Villanovo
          </p>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">Villas Trackées</p>
                <p className="text-2xl font-bold text-blue-700">
                  {stats.stats.villas_tracked || 0}
                </p>
              </div>
              <Home className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">Jours Disponibles</p>
                <p className="text-2xl font-bold text-green-700">
                  {stats.stats.available_days || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-900">Jours Réservés</p>
                <p className="text-2xl font-bold text-red-700">
                  {stats.stats.booked_days || 0}
                </p>
              </div>
              <Activity className="w-8 h-8 text-red-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-900">Dernière Sync</p>
                <p className="text-sm font-bold text-purple-700">
                  {stats.lastSync ? formatDate(stats.lastSync.started_at) : 'Jamais'}
                </p>
              </div>
              <Clock className="w-8 h-8 text-purple-600 opacity-50" />
            </div>
          </Card>
        </div>
      )}

      {/* Configuration de synchronisation */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-blue-600" />
          Lancer une Synchronisation
        </h3>

        <div className="space-y-4">
          {/* Type de synchronisation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de synchronisation
            </label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <button
                onClick={() => setSyncConfig({ syncType: 'full', selectedCountry: '', selectedZones: [], selectedVillas: [] })}
                className={`p-4 border-2 rounded-lg transition-all ${
                  syncConfig.syncType === 'full'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Globe className={`w-6 h-6 mx-auto mb-2 ${syncConfig.syncType === 'full' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-medium ${syncConfig.syncType === 'full' ? 'text-blue-900' : 'text-gray-700'}`}>
                  Complète
                </p>
                <p className="text-xs text-gray-500 mt-1">Toutes les villas actives</p>
              </button>

              <button
                onClick={() => setSyncConfig({ ...syncConfig, syncType: 'country', selectedZones: [], selectedVillas: [] })}
                className={`p-4 border-2 rounded-lg transition-all ${
                  syncConfig.syncType === 'country'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <MapPin className={`w-6 h-6 mx-auto mb-2 ${syncConfig.syncType === 'country' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-medium ${syncConfig.syncType === 'country' ? 'text-blue-900' : 'text-gray-700'}`}>
                  Par Pays
                </p>
                <p className="text-xs text-gray-500 mt-1">Filtrer par pays</p>
              </button>

              <button
                onClick={() => setSyncConfig({ ...syncConfig, syncType: 'zone', selectedVillas: [] })}
                className={`p-4 border-2 rounded-lg transition-all ${
                  syncConfig.syncType === 'zone'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <TrendingUp className={`w-6 h-6 mx-auto mb-2 ${syncConfig.syncType === 'zone' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-medium ${syncConfig.syncType === 'zone' ? 'text-blue-900' : 'text-gray-700'}`}>
                  Par Zone
                </p>
                <p className="text-xs text-gray-500 mt-1">Sélection multiple</p>
              </button>

              <button
                onClick={() => setSyncConfig({ ...syncConfig, syncType: 'manual', selectedCountry: '', selectedZones: [] })}
                className={`p-4 border-2 rounded-lg transition-all ${
                  syncConfig.syncType === 'manual'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Home className={`w-6 h-6 mx-auto mb-2 ${syncConfig.syncType === 'manual' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-medium ${syncConfig.syncType === 'manual' ? 'text-blue-900' : 'text-gray-700'}`}>
                  Manuelle
                </p>
                <p className="text-xs text-gray-500 mt-1">Sélectionner villas</p>
              </button>
            </div>
          </div>

          {/* Sélection du pays */}
          {syncConfig.syncType === 'country' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un pays
              </label>
              <select
                value={syncConfig.selectedCountry}
                onChange={(e) => setSyncConfig({ ...syncConfig, selectedCountry: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Choisir un pays --</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          )}

          {/* Sélection des zones (système à 2 étapes) */}
          {syncConfig.syncType === 'zone' && (
            <div className="space-y-4">
              {/* Étape 1: Sélection du pays */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Choisissez un pays
                </label>
                <select
                  value={syncConfig.selectedCountry}
                  onChange={(e) => setSyncConfig({ ...syncConfig, selectedCountry: e.target.value, selectedZones: [] })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Sélectionner un pays --</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              {/* Étape 2: Sélection des zones (vignettes) */}
              {syncConfig.selectedCountry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    2. Sélectionnez les zones ({syncConfig.selectedZones.length} sélectionnée(s))
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto p-2">
                    {getZonesForCountry(syncConfig.selectedCountry).map(zoneObj => {
                      const villaCount = getVillaCountByZone(zoneObj.zone);
                      const isSelected = syncConfig.selectedZones.includes(zoneObj.zone);
                      
                      return (
                        <button
                          key={zoneObj.zone}
                          onClick={() => toggleZone(zoneObj.zone)}
                          className={`p-3 border-2 rounded-lg transition-all text-left ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <p className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                              {zoneObj.zone}
                            </p>
                            {isSelected && (
                              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {villaCount} villa{villaCount > 1 ? 's' : ''}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sélection manuelle des villas */}
          {syncConfig.syncType === 'manual' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rechercher et sélectionner des villas ({syncConfig.selectedVillas.length} sélectionnée(s))
                </label>
                
                {/* Barre de recherche */}
                <input
                  type="text"
                  placeholder="Rechercher par nom ou ID..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                  onChange={(e) => {
                    const search = e.target.value.toLowerCase();
                    // On pourrait filtrer ici mais pour simplifier on affiche tout
                  }}
                />

                {/* Liste des villas avec checkbox */}
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {villas.filter(v => v.state === 1).slice(0, 100).map(villa => {
                    const isSelected = syncConfig.selectedVillas.includes(villa.id);
                    
                    return (
                      <button
                        key={villa.id}
                        onClick={() => toggleVilla(villa.id)}
                        className={`w-full p-3 border-b border-gray-200 hover:bg-gray-50 transition-colors text-left flex items-center gap-3 ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            #{villa.id} - {villa.public_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {villa.country} • {villa.zone} • {villa.number_bedrooms} ch.
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  💡 100 premières villas affichées. Utilisez la recherche pour filtrer.
                </p>
              </div>
            </div>
          )}

          {/* Bouton de synchronisation */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              onClick={handleSync}
              disabled={
                syncing || 
                (syncConfig.syncType === 'country' && !syncConfig.selectedCountry) || 
                (syncConfig.syncType === 'zone' && (!syncConfig.selectedCountry || syncConfig.selectedZones.length === 0)) ||
                (syncConfig.syncType === 'manual' && syncConfig.selectedVillas.length === 0)
              }
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
            >
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Synchronisation en cours...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Lancer la Synchronisation
                </>
              )}
            </Button>
            
            {syncConfig.syncType === 'full' && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                <span>Peut prendre plusieurs minutes</span>
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Historique des synchronisations */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-purple-600" />
          Historique des Synchronisations
        </h3>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Villas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Démarré</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Terminé</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {syncHistory.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Aucune synchronisation enregistrée
                  </td>
                </tr>
              ) : (
                syncHistory.map(sync => (
                  <tr key={sync.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getSyncStatusIcon(sync.status)}
                        {getSyncStatusBadge(sync.status)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">
                        {getSyncTypeLabel(sync.sync_type, sync.filter_country, sync.filter_zone, sync.villa_id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900 font-medium">
                        {sync.villas_processed}/{sync.villas_total}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <span className="text-green-600 font-medium">{sync.dates_added || 0}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="text-blue-600">{sync.dates_updated || 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {formatDuration(sync.duration_seconds)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">
                        {formatDate(sync.started_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">
                        {formatDate(sync.completed_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {sync.status === 'completed' && (
                          <Button
                            onClick={() => setSelectedSyncId(sync.id)}
                            variant="outline"
                            size="sm"
                            className="border-blue-600 text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Détails
                          </Button>
                        )}
                        {sync.status === 'running' && (
                          <Button
                            onClick={() => handleCancelSync(sync.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-600 text-red-700 hover:bg-red-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Annuler
                          </Button>
                        )}
                        {sync.status === 'failed' && sync.errors && (
                          <button
                            onClick={() => alert(`Erreurs:\n${sync.errors}`)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Voir erreurs
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal de détails */}
      {selectedSyncId && (
        <AdminAvailabilitySyncDetails
          syncId={selectedSyncId}
          onClose={() => setSelectedSyncId(null)}
        />
      )}
    </div>
  );
};

export default AdminAvailability;

